import { type FC, type SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const baseProps = (size: number, rest: SVGProps<SVGSVGElement>) => ({
	'width': size,
	'height': size,
	'viewBox': '0 0 24 24',
	'fill': 'none',
	'stroke': 'currentColor',
	'strokeWidth': 1.75,
	'strokeLinecap': 'round' as const,
	'strokeLinejoin': 'round' as const,
	'aria-hidden': true as const,
	'focusable': false,
	...rest,
});

export const ShieldLockIcon: FC<IconProps> = ({ size = 26, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
		<rect
			x='9'
			y='11'
			width='6'
			height='5'
			rx='1'
		/>
		<path d='M10.5 11V9.5a1.5 1.5 0 0 1 3 0V11' />
	</svg>
);

export const DatabaseIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<ellipse
			cx='12'
			cy='5'
			rx='8'
			ry='3'
		/>
		<path d='M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5' />
		<path d='M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6' />
	</svg>
);

export const TargetIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<circle
			cx='12'
			cy='12'
			r='9'
		/>
		<circle
			cx='12'
			cy='12'
			r='5'
		/>
		<circle
			cx='12'
			cy='12'
			r='1.5'
		/>
	</svg>
);

export const BuildingIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<rect
			x='5'
			y='3'
			width='14'
			height='18'
			rx='1'
		/>
		<line
			x1='9'
			y1='7'
			x2='9'
			y2='7'
		/>
		<line
			x1='15'
			y1='7'
			x2='15'
			y2='7'
		/>
		<line
			x1='9'
			y1='11'
			x2='9'
			y2='11'
		/>
		<line
			x1='15'
			y1='11'
			x2='15'
			y2='11'
		/>
		<path d='M10 21v-4h4v4' />
	</svg>
);

export const ScaleIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M12 3v18' />
		<path d='M7 21h10' />
		<path d='M5 7h14' />
		<path d='M8 7 5 13a3 3 0 0 0 6 0L8 7z' />
		<path d='M16 7l-3 6a3 3 0 0 0 6 0l-3-6z' />
	</svg>
);

export const LockIcon: FC<IconProps> = ({ size = 20, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<rect
			x='5'
			y='11'
			width='14'
			height='10'
			rx='2'
		/>
		<path d='M8 11V7a4 4 0 0 1 8 0v4' />
	</svg>
);

export const FaceScanIcon: FC<IconProps> = ({ size = 20, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M3 7V5a2 2 0 0 1 2-2h2' />
		<path d='M17 3h2a2 2 0 0 1 2 2v2' />
		<path d='M21 17v2a2 2 0 0 1-2 2h-2' />
		<path d='M7 21H5a2 2 0 0 1-2-2v-2' />
		<circle
			cx='12'
			cy='11'
			r='3'
		/>
		<path d='M8 16c1-1.5 2.5-2 4-2s3 .5 4 2' />
	</svg>
);

export const BellIcon: FC<IconProps> = ({ size = 20, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9' />
		<path d='M13.7 21a2 2 0 0 1-3.4 0' />
	</svg>
);

export const ChevronIcon: FC<IconProps> = ({ size = 16, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='m6 9 6 6 6-6' />
	</svg>
);

export const ArrowIcon: FC<IconProps> = ({ size = 16, ...rest }) => (
	<svg {...baseProps(size, { strokeWidth: 2.5, ...rest })}>
		<path d='M5 12h14' />
		<path d='m12 5 7 7-7 7' />
	</svg>
);

export const SpinnerIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg
		{...baseProps(size, { strokeWidth: 2.5, ...rest })}
		style={{ animation: 'verifySpin 0.7s linear infinite', ...(rest.style ?? {}) }}
	>
		<path d='M12 3a9 9 0 1 0 9 9' />
	</svg>
);

export const CloseIcon: FC<IconProps> = ({ size = 20, ...rest }) => (
	<svg {...baseProps(size, { strokeWidth: 2, ...rest })}>
		<path d='M18 6 6 18' />
		<path d='m6 6 12 12' />
	</svg>
);

export const DocumentIcon: FC<IconProps> = ({ size = 24, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z' />
		<path d='M14 3v5h5' />
		<path d='M9 13h6' />
		<path d='M9 17h6' />
	</svg>
);

export const CheckmarkIcon: FC<IconProps> = ({ size = 14, ...rest }) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 14 14'
		fill='none'
		aria-hidden
		focusable={false}
		{...rest}
	>
		<path
			d='M2.5 7L5.5 10L11.5 4'
			stroke='#ffffff'
			strokeWidth='2.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);
